import { api } from './api';

export interface EligibilityRequirement {
  id: string;
  label: string;
  met: boolean;
  explanation: string;
  action?: string;
  actionUrl?: string;
}

export interface EligibilityResult {
  eligible: boolean;
  onboardingComplete: boolean;
  requirements: EligibilityRequirement[];
}

export interface OnboardingStatusResponse {
  data: EligibilityResult;
}

export interface OnboardingIdentity {
  coachingStyle: string;
  styleOfPlay: string;
  description?: string;
}

export interface OnboardingFormation {
  preferred: string[];
  teamShape: string;
  notes?: string;
}

export interface OnboardingInPossession {
  philosophy: string;
  pressingTriggers: string[];
  buildUpStyle: string;
}

export interface OnboardingOutOfPossession {
  philosophy: string;
  defensiveStructure: string;
  pressingApproach: string;
}

export interface OnboardingTransitions {
  attackToDefence: string;
  defenceToAttack: string;
}

export interface OnboardingPrinciples {
  developmentPriorities: string[];
  coachObjectives: string[];
  seasonGoals: string[];
}

export interface OnboardingTrainingLogistics {
  daysPerWeek: number;
  sessionDurationMinutes: number;
  ageGroup: string;
  competitiveLevel: string;
}

export interface OnboardingContext {
  identity: OnboardingIdentity;
  formation: OnboardingFormation;
  inPossession: OnboardingInPossession;
  outOfPossession: OnboardingOutOfPossession;
  transitions: OnboardingTransitions;
  principles: OnboardingPrinciples;
  trainingLogistics: OnboardingTrainingLogistics;
}

export interface OnboardingWizardStatus {
  onboardingComplete: boolean;
  completedSteps: number[];
}

export type JobState = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';

export interface JobStatusResult {
  jobId: string;
  state: JobState;
  planId?: string;
  failureReason?: {
    code: string;
    message: string;
    retryable: boolean;
  };
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface PlanSummary {
  planId: string;
  status: string;
  overview: {
    summary: string;
    keyWeaknesses: string[];
    rationale: string;
    blockDurationWeeks: number;
  };
  createdAt: string;
}

export interface SessionActivity {
  description: string;
  durationMinutes: number;
  setup?: string;
  keyCoachingPoints?: string[];
}

export interface SessionPlan {
  sessionId: string;
  weekNumber: number;
  sessionNumber: number;
  title: string;
  objective: string;
  warmUp: SessionActivity;
  mainPractice: SessionActivity;
  conditionedGame: SessionActivity;
  coachingPoints: string[];
  progressions: string[];
  regressions: string[];
  durationMinutes: number;
}

export interface WeeklyTheme {
  weekNumber: number;
  focusArea: string;
  progressionLogic: string;
  coachingObjectives: string[];
}

export interface DrillRecommendation {
  sessionId: string;
  drillId?: string;
  title: string;
  description: string;
  focusArea: string;
}

export interface VideoRecommendation {
  sessionId: string;
  videoId?: string;
  title: string;
  url?: string;
  relevance: string;
}

export interface PlanOverview {
  summary: string;
  keyWeaknesses: string[];
  rationale: string;
  blockDurationWeeks: number;
}

export interface GenerationMetadata {
  model: string;
  promptVersion: string;
  promptRegistryId: string;
  taskType: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  durationMs: number;
  validationWarnings: string[];
  qualityFlags: string[];
  contextSummary: string;
  contentHash: string;
}

export interface PlanEdit {
  editId: string;
  sectionType: 'sessionPlan' | 'weeklyTheme' | 'coachingPoint' | 'overview';
  sectionId: string;
  originalContent: Record<string, unknown>;
  modifiedContent: Record<string, unknown>;
  editedAt: string;
  coachId: string;
}

export type PlanStatus = 'generated' | 'reviewed' | 'accepted' | 'regenerated' | 'archived';

export interface DevelopmentPlan {
  planId: string;
  coachId: string;
  teamId: string;
  schemaVersion: string;
  status: PlanStatus;
  overview: PlanOverview;
  weeklyThemes: WeeklyTheme[];
  sessionPlans: SessionPlan[];
  recommendations: {
    drills: DrillRecommendation[];
    videos: VideoRecommendation[];
  };
  generationMetadata: GenerationMetadata;
  editHistory: PlanEdit[];
  createdAt: string;
  updatedAt: string;
}

export interface PlanEditRequest {
  sectionType: 'sessionPlan' | 'weeklyTheme' | 'coachingPoint' | 'overview';
  sectionId: string;
  modifiedContent: Record<string, unknown>;
}

export const aiIntelligenceService = {
  /**
   * Fetch the onboarding/eligibility status for the current coach.
   * GET /api/ai-intelligence/develop/onboarding-status
   */
  async getOnboardingStatus(teamId?: string): Promise<EligibilityResult> {
    const params = teamId ? { teamId } : {};
    const response = await api.get<OnboardingStatusResponse>(
      '/ai-intelligence/develop/onboarding-status',
      { params }
    );
    return response.data.data;
  },

  /**
   * Submit the full onboarding context.
   * POST /api/ai-intelligence/develop/onboarding
   */
  async submitOnboarding(teamId: string, data: OnboardingContext): Promise<void> {
    await api.post('/ai-intelligence/develop/onboarding', data, {
      params: { teamId },
    });
  },

  /**
   * Save a partial wizard step.
   * PATCH /api/ai-intelligence/develop/onboarding/step/:step
   */
  async savePartialStep(teamId: string, step: number, stepData: Record<string, unknown>): Promise<void> {
    await api.patch(`/ai-intelligence/develop/onboarding/step/${step}`, {
      stepData,
    }, {
      params: { teamId },
    });
  },

  /**
   * Get the wizard status (completed steps for resume support).
   * GET /api/ai-intelligence/develop/onboarding-status
   */
  async getWizardStatus(teamId: string): Promise<OnboardingWizardStatus> {
    const params = { teamId };
    const response = await api.get<{ data: OnboardingWizardStatus }>(
      '/ai-intelligence/develop/onboarding-status',
      { params }
    );
    return response.data.data;
  },

  /**
   * Initiate plan generation.
   * POST /api/ai-intelligence/develop/generate-plan
   * Returns 202 with jobId.
   */
  async generatePlan(teamId: string): Promise<string> {
    const response = await api.post<{ data: { jobId: string } }>(
      '/ai-intelligence/develop/generate-plan',
      { teamId }
    );
    return response.data.data.jobId;
  },

  /**
   * Poll job status.
   * GET /api/ai-intelligence/develop/plans/:id/status
   */
  async getJobStatus(jobId: string): Promise<JobStatusResult> {
    const response = await api.get<{ data: JobStatusResult }>(
      `/ai-intelligence/develop/plans/${jobId}/status`
    );
    return response.data.data;
  },

  /**
   * List previously generated plans.
   * GET /api/ai-intelligence/develop/plans
   */
  async listPlans(teamId?: string): Promise<PlanSummary[]> {
    const params = teamId ? { teamId } : {};
    const response = await api.get<{ data: PlanSummary[] }>(
      '/ai-intelligence/develop/plans',
      { params }
    );
    return response.data.data;
  },

  /**
   * Get full plan detail.
   * GET /api/ai-intelligence/develop/plans/:id
   */
  async getPlan(planId: string): Promise<DevelopmentPlan> {
    const response = await api.get<{ data: DevelopmentPlan }>(
      `/ai-intelligence/develop/plans/${planId}`
    );
    return response.data.data;
  },

  /**
   * Edit a section of a plan.
   * PATCH /api/ai-intelligence/develop/plans/:id
   */
  async editPlan(planId: string, edit: PlanEditRequest): Promise<void> {
    await api.patch(`/ai-intelligence/develop/plans/${planId}`, edit);
  },

  /**
   * Update plan status.
   * PATCH /api/ai-intelligence/develop/plans/:id/status
   */
  async updatePlanStatus(planId: string, status: PlanStatus): Promise<void> {
    await api.patch(`/ai-intelligence/develop/plans/${planId}/status`, { status });
  },
};
